import React, { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { HoverInsight } from '../types';
import MetricCard from '../components/MetricCard';
import api from '../lib/api';
import { formatChartDate } from '../lib/formatDate';
import { DateRangePicker } from '../components/DateRangePicker';
import { useGlobalDateRange, clampRangeToBounds } from '../context/GlobalDateRangeContext';
import { Tabs as TabsBase, TabsList as TabsListBase, TabsTrigger as TabsTriggerBase, TabsContent as TabsContentBase } from '../components/ui/tabs';
import { Radio, Megaphone, RotateCw } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip as ChartTooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';

interface MetaAdsProps {
  onHover: (insight: HoverInsight | null) => void;
}

interface GroupSummary {
  group: string;
  spend: number;
  revenue: number;
  orders: number;
  roas: number;
  aov: number;
}

interface Campaign extends GroupSummary {
  campaign: string;
}

interface AccountData {
  spend: number;
  revenue: number;
  orders: number;
  roas: number;
  aov: number;
  cac: number;
  daily_trend: { date: string; spend: number; revenue: number }[];
  campaigns: Campaign[];
  group_summary: GroupSummary[];
}

interface LiveResponse {
  connected: boolean;
  fetched_at: string | null;
  accounts: Record<string, AccountData>;
}

const inr = (n: number) => `₹${(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const toISO = (d: Date) => format(d, 'yyyy-MM-dd');
const Tabs: any = TabsBase;
const TabsList: any = TabsListBase;
const TabsTrigger: any = TabsTriggerBase;
const TabsContent: any = TabsContentBase;

const GROUP_STYLES: Record<string, string> = {
  DPA: 'bg-violet-50 text-violet-700 border-violet-100',
  Retargeting: 'bg-cyan-50 text-cyan-700 border-cyan-100',
  'Generic Ads': 'bg-amber-50 text-amber-700 border-amber-100',
};

const GroupBadge = ({ group }: { group: string }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${GROUP_STYLES[group] || 'bg-slate-50 text-slate-600 border-slate-100'}`}>
    {group}
  </span>
);

function AccountPanel({ accountName, data, onHover }: { accountName: string; data: AccountData; onHover: (i: HoverInsight | null) => void }) {
  const dailyTrend = useMemo(() => (data.daily_trend || []).map((d) => ({
    date: formatChartDate(d.date),
    Spend: d.spend,
    Revenue: d.revenue,
  })), [data]);

  return (
    <div className="space-y-6" data-testid={`meta-ads-panel-${accountName.replace(/\W+/g, '-').toLowerCase()}`}>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <MetricCard platform="Meta Ads" title="Spend" value={inr(data.spend)} change="Selected window" trend="up"
          insight={{ title: `${accountName} — Spend`, metric: 'Spend', value: inr(data.spend), aiInsight: `${inr(data.spend)} spent on ${accountName} in the selected window.`, prediction: 'Trend requires more history to project.', nextSteps: ['Compare against Revenue and ROAS alongside.'] }}
          onHover={onHover} />
        <MetricCard platform="Meta Ads" title="Revenue" value={inr(data.revenue)} change="Attributed purchases" trend="up"
          insight={{ title: `${accountName} — Revenue`, metric: 'Revenue', value: inr(data.revenue), aiInsight: `${inr(data.revenue)} in purchase revenue attributed to ${accountName}.`, prediction: 'Trend requires more history to project.', nextSteps: ['Check the Top Performing Campaigns table below.'] }}
          onHover={onHover} />
        <MetricCard platform="Meta Ads" title="ROAS" value={`${data.roas.toFixed(2)}x`} change="Revenue / Spend" trend={data.roas >= 1 ? 'up' : 'down'}
          insight={{ title: `${accountName} — ROAS`, metric: 'ROAS', value: `${data.roas.toFixed(2)}x`, aiInsight: `Every ₹1 spent on ${accountName} returned ₹${data.roas.toFixed(2)} in revenue.`, prediction: 'Trend requires more history to project.', nextSteps: ['Compare ROAS across DPA / Retargeting / Generic Ads below.'] }}
          onHover={onHover} />
        <MetricCard platform="Meta Ads" title="Orders" value={data.orders.toLocaleString('en-IN')} change="Attributed" trend="up"
          insight={{ title: `${accountName} — Orders`, metric: 'Orders', value: String(data.orders), aiInsight: `${data.orders} purchases attributed to ${accountName} in the selected window.`, prediction: 'Trend requires more history to project.', nextSteps: ['Compare AOV and CAC alongside.'] }}
          onHover={onHover} />
        <MetricCard platform="Meta Ads" title="AOV" value={inr(data.aov)} change="Revenue / Orders" trend="neutral"
          insight={{ title: `${accountName} — AOV`, metric: 'AOV', value: inr(data.aov), aiInsight: `Average order value of ${inr(data.aov)} for purchases attributed to ${accountName}.`, prediction: 'Trend requires more history to project.', nextSteps: ['Compare against Shopify-wide AOV in Shopify DTC.'] }}
          onHover={onHover} />
        <MetricCard platform="Meta Ads" title="CAC" value={inr(data.cac)} change="Spend / Orders" trend={data.cac > data.aov ? 'down' : 'neutral'}
          insight={{ title: `${accountName} — CAC`, metric: 'CAC', value: inr(data.cac), aiInsight: `Customer acquisition cost of ${inr(data.cac)} per attributed order on ${accountName}.`, prediction: 'Trend requires more history to project.', nextSteps: ['A healthy account keeps CAC comfortably below AOV.'] }}
          onHover={onHover} />
      </div>

      <div className="bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm space-y-4">
        <h3 className="font-display font-semibold text-slate-700 text-sm tracking-wide uppercase">Spend vs Revenue Over Time</h3>
        <div className="h-64 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={dailyTrend} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="date" stroke="#94a3b8" fontSize={10} tickLine={false} />
              <YAxis stroke="#94a3b8" fontSize={10} tickLine={false} />
              <ChartTooltip contentStyle={{ background: '#ffffff', borderColor: '#e2e8f0', borderRadius: '12px' }} formatter={(v: number) => inr(v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="Spend" stroke="#1877F2" strokeWidth={2.5} dot={false} />
              <Line type="monotone" dataKey="Revenue" stroke="#95BF47" strokeWidth={2.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm space-y-4" data-testid="meta-ads-group-summary">
        <h3 className="font-display font-semibold text-slate-700 text-sm tracking-wide uppercase">DPA vs Retargeting vs Generic Ads</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {data.group_summary.map((g) => (
            <div key={g.group} className="rounded-2xl border border-slate-100 p-4 space-y-2">
              <GroupBadge group={g.group} />
              <p className="text-xl font-bold text-slate-800 font-mono">{inr(g.spend)}</p>
              <p className="text-[11px] text-slate-400">Spend</p>
              <div className="grid grid-cols-2 gap-2 pt-1 text-xs">
                <div><span className="text-slate-400">Revenue</span><p className="font-semibold text-slate-700">{inr(g.revenue)}</p></div>
                <div><span className="text-slate-400">ROAS</span><p className="font-semibold text-slate-700">{g.roas.toFixed(2)}x</p></div>
                <div><span className="text-slate-400">Orders</span><p className="font-semibold text-slate-700">{g.orders}</p></div>
                <div><span className="text-slate-400">AOV</span><p className="font-semibold text-slate-700">{inr(g.aov)}</p></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm space-y-4" data-testid="meta-ads-campaign-table">
        <h3 className="font-display font-semibold text-slate-700 text-sm tracking-wide uppercase">Top Performing Campaigns</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] font-mono uppercase text-slate-400 border-b border-slate-100">
                <th className="py-2 pr-4">Campaign</th>
                <th className="py-2 pr-4">Group</th>
                <th className="py-2 pr-4 text-right">Spend</th>
                <th className="py-2 pr-4 text-right">Revenue</th>
                <th className="py-2 pr-4 text-right">ROAS</th>
                <th className="py-2 pr-4 text-right">Orders</th>
                <th className="py-2 text-right">AOV</th>
              </tr>
            </thead>
            <tbody>
              {data.campaigns.map((c) => (
                <tr key={c.campaign} className="border-b border-slate-50 last:border-0">
                  <td className="py-2.5 pr-4 text-slate-700 max-w-xs truncate" title={c.campaign}>{c.campaign}</td>
                  <td className="py-2.5 pr-4"><GroupBadge group={c.group} /></td>
                  <td className="py-2.5 pr-4 text-right font-semibold text-slate-800">{inr(c.spend)}</td>
                  <td className="py-2.5 pr-4 text-right text-slate-600">{inr(c.revenue)}</td>
                  <td className="py-2.5 pr-4 text-right text-slate-600">{c.roas.toFixed(2)}x</td>
                  <td className="py-2.5 pr-4 text-right text-slate-600">{c.orders}</td>
                  <td className="py-2.5 text-right text-slate-600">{inr(c.aov)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function MetaAds({ onHover }: MetaAdsProps) {
  const [data, setData] = useState<LiveResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeAccount, setActiveAccount] = useState<string>('');

  const today = useMemo(() => new Date(), []);
  const ninetyDaysAgo = useMemo(() => { const d = new Date(); d.setDate(d.getDate() - 90); return d; }, []);
  const { globalRange } = useGlobalDateRange();
  const [range, setRange] = useState<{ from: Date; to: Date }>(() => clampRangeToBounds(globalRange, ninetyDaysAgo, today));
  const [syncing, setSyncing] = useState(false);

  const loadLive = (isManualSync = false) => {
    if (isManualSync) setSyncing(true); else setLoading(true);
    // Queries Windsor.ai LIVE on every request (no Mongo cache) so numbers match the client's own
    // Meta Ads dashboard 1:1 for whatever date range is selected — same "Choice B" pattern used
    // for Shopify's ShopifyQL-backed Financials/Sessions/Acquisition tabs. The "Manual Sync" button
    // simply re-runs this exact call — there's no cache to invalidate, every call already hits
    // Windsor fresh, the button just gives an explicit, visible re-fetch on demand.
    return api.get('/revenue-ops/meta-ads/live', { params: { start_date: toISO(range.from), end_date: toISO(range.to) } })
      .then((res) => {
        setData(res.data);
        const accountNames = Object.keys(res.data?.accounts || {});
        if (accountNames.length > 0) setActiveAccount((prev) => (accountNames.includes(prev) ? prev : accountNames[0]));
      })
      .catch(() => setData(null))
      .finally(() => { setLoading(false); setSyncing(false); });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadLive(false); }, [range]);

  const accountNames = Object.keys(data?.accounts || {});

  return (
    <div className="space-y-8 animate-fade-in font-sans -m-6 md:-m-8 p-6 md:p-8 min-h-full" style={{ background: 'linear-gradient(180deg, #F7EFE3 0%, #F3E8D8 100%)' }} data-testid="meta-ads-module-root">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-[#e2d3ba] pb-6">
        <div>
          <span className="text-[10px] font-mono uppercase tracking-widest text-[#9b7d54] block mb-1">Paid Social Performance</span>
          <h1 className="text-3xl font-semibold font-arial text-slate-800 tracking-tight">Meta Ads</h1>
          <p className="text-xs text-slate-500 mt-1 font-sans">Live via Windsor.ai — each account queried separately, in real time, never blended or cached.</p>
        </div>
        <div className="flex items-center gap-3">
          <DateRangePicker value={range} onChange={setRange} minDate={ninetyDaysAgo} maxDate={today} testIdPrefix="meta-ads" />
          <button
            data-testid="meta-ads-manual-sync-button"
            onClick={() => loadLive(true)}
            disabled={syncing || loading}
            className="px-3.5 py-2.5 rounded-xl flex items-center gap-2 text-xs font-semibold border bg-white text-blue-700 border-blue-100 hover:border-blue-300 transition-colors disabled:opacity-50"
          >
            <RotateCw className={`h-3.5 w-3.5 ${syncing ? 'animate-spin' : ''}`} />
            <span>{syncing ? 'Syncing…' : 'Manual Sync'}</span>
          </button>
          <div data-testid="meta-ads-connection-badge" className={`px-4 py-2.5 rounded-xl flex items-center gap-2.5 text-xs font-semibold border ${data?.connected ? 'bg-blue-50 text-blue-700 border-blue-100' : 'bg-slate-100 text-slate-400 border-slate-200'}`}>
            <Radio className={`h-4.5 w-4.5 ${data?.connected ? 'animate-pulse' : ''}`} />
            <span data-testid="meta-ads-fetched-at">{data?.connected ? `Live · ${data.fetched_at ? new Date(data.fetched_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : ''}` : 'Not Connected'}</span>
          </div>
        </div>
      </div>

      {loading ? (
        <div data-testid="meta-ads-loading" className="bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm text-xs text-slate-400 font-mono">Querying Windsor.ai live…</div>
      ) : !data?.connected || accountNames.length === 0 ? (
        <div data-testid="meta-ads-disconnected" className="bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm text-xs text-slate-500">
          Meta Ads data isn't available yet. Ask your admin to confirm the Windsor.ai connection.
        </div>
      ) : (
        <Tabs value={activeAccount} onValueChange={setActiveAccount} data-testid="meta-ads-account-tabs">
          <TabsList className="bg-slate-100 p-1 rounded-xl">
            {accountNames.map((name) => (
              <TabsTrigger key={name} value={name} data-testid={`meta-ads-tab-${name.replace(/\W+/g, '-').toLowerCase()}`} className="text-xs font-semibold rounded-lg flex items-center gap-1.5">
                <Megaphone className="h-3 w-3" /> {name}
              </TabsTrigger>
            ))}
          </TabsList>
          {accountNames.map((name) => (
            <TabsContent key={name} value={name} className="mt-6">
              <AccountPanel accountName={name} data={data!.accounts[name]} onHover={onHover} />
            </TabsContent>
          ))}
        </Tabs>
      )}
    </div>
  );
}
